import React, { useEffect } from "react";
import {
    useSubmit,
    useNavigation,
    useActionData,
    useNavigate,
} from "react-router";
import { Card, Button, Form, FloatingLabel } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ChangePasswordSchema } from "schema/account";
import { renderSavingProgressToast } from "components/shared/Toasts";
import axios from "axios";
import BackLink from "./BackLink";

export async function action({ request }) {
    const data = await request.json();

    try {
        // Prepare HTTP Basic Auth header with current:new password format
        const basicAuth = btoa(
            `_:${data.current_password}:${data.new_password}`
        );

        await renderSavingProgressToast(
            axios.put(
                "account/change-password",
                {}, // Empty body since credentials are in header
                {
                    headers: {
                        Authorization: `Basic ${basicAuth}`,
                    },
                }
            ),
            {
                success: "Updated password successfully",
                error: "Updated password failed",
            }
        );
        return { success: true };
    } catch (error) {
        if (error.response?.data) {
            const errorData = error.response.data;
            return {
                success: false,
                reason: errorData["description"] || "Failed to change password",
            };
        }

        return Promise.reject(error);
    }
}

const ChangePassword = () => {
    const submit = useSubmit();
    const actionData = useActionData();
    const navigation = useNavigation();
    const navigate = useNavigate();
    const isNavigating =
        navigation.state === "submitting" || navigation.state === "loading";

    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useForm({
        resolver: yupResolver(ChangePasswordSchema),
        defaultValues: {
            current_password: "",
            new_password: "",
            confirm_password: "",
        },
    });

    useEffect(() => {
        if (actionData?.success) {
            navigate("/account");
        }
    }, [actionData, navigate]);

    const onSubmit = async (data) => {
        await submit(data, {
            action: "/account/change-password",
            method: "post",
            encType: "application/json",
        });
    };

    const onError = (errors, e) => console.error(errors, e);

    return (
        <div style={{ width: "100%", maxWidth: "450px", margin: "0 auto" }}>
            <BackLink to="/account" />
            <Card className="mt-3" style={{ width: "100%" }} border="secondary">
                <Card.Header>Change Password</Card.Header>
                <Card.Body>
                    <Form
                        onSubmit={handleSubmit(onSubmit, onError)}
                        onReset={reset}
                    >
                        <Form.Group className="mb-3">
                            <Controller
                                control={control}
                                name="current_password"
                                render={({
                                    field: { onChange, value, ref },
                                }) => (
                                    <FloatingLabel
                                        controlId="current_password"
                                        label="Current Password"
                                    >
                                        <Form.Control
                                            type="password"
                                            name="current_password"
                                            isInvalid={errors.current_password}
                                            placeholder="Current Password"
                                            onChange={onChange}
                                            value={value}
                                            ref={ref}
                                            data-testid="input_current_password"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.current_password?.message}
                                        </Form.Control.Feedback>
                                    </FloatingLabel>
                                )}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Controller
                                control={control}
                                name="new_password"
                                render={({
                                    field: { onChange, value, ref },
                                }) => (
                                    <FloatingLabel
                                        controlId="new_password"
                                        label="New Password"
                                    >
                                        <Form.Control
                                            type="password"
                                            name="new_password"
                                            isInvalid={errors.new_password}
                                            placeholder="New Password"
                                            onChange={onChange}
                                            value={value}
                                            ref={ref}
                                            data-testid="input_new_password"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.new_password?.message}
                                        </Form.Control.Feedback>
                                    </FloatingLabel>
                                )}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Controller
                                control={control}
                                name="confirm_password"
                                render={({
                                    field: { onChange, value, ref },
                                }) => (
                                    <FloatingLabel
                                        controlId="confirm_password"
                                        label="Confirm Password"
                                    >
                                        <Form.Control
                                            type="password"
                                            name="confirm_password"
                                            isInvalid={errors.confirm_password}
                                            placeholder="Confirm Password"
                                            onChange={onChange}
                                            value={value}
                                            ref={ref}
                                            data-testid="input_confirm_password"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.confirm_password?.message}
                                        </Form.Control.Feedback>
                                    </FloatingLabel>
                                )}
                            />
                        </Form.Group>
                        {actionData?.reason && (
                            <div className="alert alert-danger" role="alert">
                                {actionData.reason}
                            </div>
                        )}
                        <div className="d-grid gap-2">
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={isNavigating}
                            >
                                {isNavigating
                                    ? "Changing..."
                                    : "Change Password"}
                            </Button>
                            <Button
                                type="reset"
                                variant="secondary"
                                disabled={isNavigating}
                            >
                                Clear
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </div>
    );
};

export default ChangePassword;
